export type Language = 'ca' | 'es' | 'en';

export interface ProjectItem {
  category: string;
  sectorId: string;
  title: string;
  description: string;
  tags: string[];
  highlight: string;
  demoUrl: string;
}

export type TranslationType = {
  header: {
    solutions: string;
    whyWeb: string;
    roi: string;
    projects: string;
    pricing: string;
    contact: string;
    cta: string;
  };
  hero: {
    badge: string;
    headlinePart1: string;
    headlineHighlight: string;
    sub: string;
    ctaPrimary: string;
    ctaSecondary: string;
    pill1: string;
    pill2: string;
    pill3: string;
    mockup: {
      category: string;
      status: string;
      title: string;
      description: string;
      ctaPrimary: string;
      ctaSecondary: string;
      reviewsText: string;
      locationText: string;
      aiTitle: string;
      aiText: string;
      floatingTag: string;
      floatingPrice: string;
    };
  };
  socialVsWeb: {
    badge: string;
    title: string;
    sub: string;
    socialTag: string;
    socialTitle: string;
    socialSubtitle: string;
    socialPoints: string[];
    socialQuote: string;
    webTag: string;
    webTitle: string;
    webSubtitle: string;
    webPoints: string[];
    webQuote: string;
  };
  benefits: {
    badge: string;
    title: string;
    sub: string;
    items: {
      title: string;
      desc: string;
      icon: string;
    }[];
  };
  solutions: {
    badge: string;
    title: string;
    sub: string;
    fromLabel: string;
    whatsIncluded: string;
    recommendedFor: string;
    p1Name: string;
    p1Badge: string;
    p1SubBadge: string;
    p1Price: string;
    p1Period: string;
    p1Desc: string;
    p1Features: string[];
    p1Ideal: string;
    p1Cta: string;
    p2Name: string;
    p2Badge: string;
    p2SubBadge: string;
    p2Price: string;
    p2Period: string;
    p2Desc: string;
    p2FeaturesTitle: string;
    p2Features: string[];
    p2Ideal: string;
    p2Cta: string;
  };
  roiCalculator: {
    badge: string;
    title: string;
    sub: string;
    ticketLabel: string;
    clientsLabel: string;
    clientsPerMonth: string;
    solutionLabel: string;
    solutionP1: string;
    solutionP2: string;
    resultTitle: string;
    clientsNeededText: string;
    clientsNeededPrefix: string;
    clientsNeededSuffix: string;
    clientsExplanation: string;
    monthlyExtraText: string;
    paybackTimeText: string;
    monthUnitSingular: string;
    monthUnitPlural: string;
    sixMonthsText: string;
    twelveMonthsText: string;
    disclaimer: string;
    ctaWhatsApp: string;
  };
  projects: {
    badge: string;
    title: string;
    sub: string;
    allDemosBanner: string;
    allDemosButton: string;
    openDemoButton: string;
    askCustomButton: string;
    keyHighlight: string;
    categories: { id: string; label: string }[];
    items: ProjectItem[];
  };
  addons: {
    badge: string;
    title: string;
    sub: string;
    infoButton: string;
    items: {
      title: string;
      price: string;
      desc: string;
      points: string[];
    }[];
  };
  process: {
    badge: string;
    title: string;
    sub: string;
    steps: {
      num: string;
      title: string;
      desc: string;
    }[];
  };
  pricing: {
    badge: string;
    title: string;
    sub: string;
    fromLabel: string;
    whatsIncluded: string;
    plan1: {
      tag: string;
      subTag: string;
      name: string;
      price: string;
      period: string;
      features: string[];
      optionalText: string;
      cta: string;
    };
    plan2: {
      tag: string;
      subTag: string;
      name: string;
      price: string;
      period: string;
      features: string[];
      cta: string;
    };
    noteTitle: string;
    noteText: string;
  };
  faq: {
    badge: string;
    title: string;
    sub: string;
    extraQuestionTitle: string;
    extraQuestionText: string;
    extraQuestionButton: string;
    items: {
      q: string;
      a: string;
    }[];
  };
  contact: {
    badge: string;
    title: string;
    sub: string;
    formTitle: string;
    nameLabel: string;
    namePlaceholder: string;
    businessLabel: string;
    businessPlaceholder: string;
    phoneLabel: string;
    phonePlaceholder: string;
    emailLabel: string;
    solutionLabel: string;
    solutionOptions: { value: string; label: string }[];
    messageLabel: string;
    messagePlaceholder: string;
    submitBtn: string;
    submittingBtn: string;
    privacyNote: string;
    successTitle: string;
    successDesc: string;
    openWhatsAppBtn: string;
    directBadge: string;
    directTitle: string;
    directDesc: string;
    whatsappBtn: string;
    callBtn: string;
    commitmentTitle: string;
    commitmentPoints: string[];
  };
  footer: {
    tagline: string;
    solutionsTitle: string;
    solutionsList: string[];
    navTitle: string;
    linksList: string[];
    contactBadge: string;
    phoneLabel: string;
    rights: string;
    legal: string[];
  };
  whatsapp: {
    tooltip: string;
    message: string;
  };
  marquee: string[];
  stats: {
    badge: string;
    deliveryLabel: string;
    deliverySub: string;
    projectsLabel: string;
    projectsSub: string;
    automationsLabel: string;
    automationsSub: string;
    uptimeLabel: string;
    uptimeSub: string;
  };
  testimonials: {
    quote: string;
    name: string;
    role: string;
    metric: string;
  };
};

export const translations: Record<Language, TranslationType> = {
  ca: {
    header: {
      solutions: 'Solucions',
      whyWeb: 'Per què una web',
      roi: 'Calculadora ROI',
      projects: 'Demos per sectors',
      pricing: 'Preus',
      contact: 'Contacte',
      cta: 'Vull la meua web',
    },
    hero: {
      badge: 'Web + IA + Automatització per a negocis que volen créixer',
      headlinePart1: 'La teua presència digital comença amb un',
      headlineHighlight: 'lloc propi.',
      sub: 'Creem webs i sistemes digitals perquè els clients et troben, entenguen què ofereixes i puguen contactar-te sense complicacions.',
      ctaPrimary: 'Vull millorar la presència del meu negoci',
      ctaSecondary: 'Veure les solucions',
      pill1: 'Sense quotes obligatòries',
      pill2: 'Lliurament ràpid i optimitzat',
      pill3: 'Optimitzat per a Google i IA',
      mockup: {
        category: 'Restaurant & Cafè Local',
        status: '● Obert ara',
        title: 'Cuina de Mercat & Producte Fresc',
        description: 'Menú diari, carta digital interactiva i reserves immediates per WhatsApp.',
        ctaPrimary: '💬 WhatsApp',
        ctaSecondary: 'Veure carta',
        reviewsText: '(148 ressenyes a Google)',
        locationText: 'Carrer Major, 12 · València',
        aiTitle: 'Optimitzat per a nous cercadors',
        aiText: 'ChatGPT, Perplexity i Gemini entenen els teus horaris i serveis.',
        floatingTag: 'Domini Propi Inclòs',
        floatingPrice: 'Des de 390 € · Pagament únic',
      },
    },
    socialVsWeb: {
      badge: 'El problema de no tenir web pròpia',
      title: 'Xarxes socials o espai propi?',
      sub: 'Molts negocis depenen únicament d’Instagram, TikTok o WhatsApp. Aquestes plataformes sumen, però no són el teu territori.',
      socialTag: 'La realitat actual',
      socialTitle: 'Dependre només de xarxes socials',
      socialSubtitle: 'La teua visibilitat depèn de regles que no controles',
      socialPoints: [
        'Depens al 100% de canvis constants d’algoritme.',
        'Menys del 10% dels teus seguidors veuen el que publiques.',
        'La informació clau (serveis, horaris, adreça) queda enterrada.',
        'Si no saben el teu nom exacte a Instagram, no et troben a Google.',
        'La plataforma pot tancar el teu compte o canviar les normes d’un dia per l’altre.',
      ],
      socialQuote: '«Si només ets a Instagram, depens d’un algoritme que pot reduir el teu abast en qualsevol moment.»',
      webTag: 'L’actiu propi',
      webTitle: 'Tenir la teua pròpia web amb Onix',
      webSubtitle: 'Un actiu sòlid que treballa per al teu negoci 24/7',
      webPoints: [
        'És el teu espai oficial: ningú et pot canviar les regles.',
        'Tota la informació clau ordenada: fotos, serveis, carta, horaris i ressenyes.',
        'Apareix a Google Maps i cerques locals quan algú cerca el teu servei a la teua zona.',
        'Estructura neta perquè els nous cercadors d’IA comprenguen el teu negoci.',
        'Centralitza contactes, trucades i missatges de WhatsApp en un sol clic.',
      ],
      webQuote: '★ Un lloc oficial que dóna serietat, centralitza contactes i converteix visites en trucades i cites.',
    },
    benefits: {
      badge: 'Valor real',
      title: 'Què fa una web pel teu negoci',
      sub: 'Una web no és una despesa decorativa: és l’eina que fa que els clients et coneguen, confien i prenguen la decisió de contactar-te.',
      items: [
        {
          title: 'Més fàcil de trobar',
          desc: 'Els clients que busquen el teu servei a Google o Maps troben el teu negoci abans que el de la competència.',
          icon: 'MagnifyingGlassIcon',
        },
        {
          title: 'Explica què ofereixes en 5 segons',
          desc: 'Sense que el visitant s’haja de perdre buscant destacats a Instagram: serveis, preus i fotos clares a l’instant.',
          icon: 'CheckBadgeIcon',
        },
        {
          title: 'Transmet confiança professional',
          desc: 'Un negoci amb domini propi i disseny impecable genera seguretat immediata davant d’un client nou.',
          icon: 'ShieldCheckIcon',
        },
        {
          title: 'Centralitza tot el teu contingut',
          desc: 'Horaris actualitzats, ubicació interactiva, preguntes freqüents, ressenyes reals i catàleg en un sol punt.',
          icon: 'Squares2X2Icon',
        },
        {
          title: 'Contacte en un sol clic',
          desc: 'Botó directe de WhatsApp, trucada directa i formulari senzill pensats per eliminar qualsevol fricció.',
          icon: 'ChatBubbleLeftRightIcon',
        },
        {
          title: 'Llest per als cercadors d’IA',
          desc: 'Dades estructurades perquè ChatGPT, Google Gemini i els nous assistents digitals recomanen el teu negoci.',
          icon: 'SparklesIcon',
        },
      ],
    },
    solutions: {
      badge: 'Solucions adaptades',
      title: 'Dues maneres de fer créixer el teu negoci',
      sub: 'Des d’una presència impecable per començar a ser visible, fins a una eina a mida per automatitzar reserves i processos.',
      fromLabel: 'Des de',
      whatsIncluded: 'Què pot incloure:',
      recommendedFor: 'Recomanat per a:',
      p1Name: 'Onix Presència',
      p1Badge: 'El teu punt de partida digital',
      p1SubBadge: 'Sense quota mensual',
      p1Price: 'Des de 390 €',
      p1Period: 'pagament únic · sense quotes obligatòries',
      p1Desc: 'Una landing page d’alt impacte dissenyada per a negocis que volen una presència clara, atractiva i fàcil de trobar.',
      p1Features: [
        'Disseny professional d’una pàgina adaptada 100% a mòbil',
        'Presentació del negoci, equip i serveis destacats',
        'Horaris, adreça, telèfon i Google Maps interactiu',
        'Botons d’acció ràpida per a trucades i WhatsApp directe',
        'Formulari de contacte ràpid connectat al teu correu',
        'Galeria de fotos d’alta qualitat i enllaços a xarxes',
        'SEO tècnic bàsic per a Google i assistents d’IA',
        'Publicació amb domini propi i certificat SSL segur',
        '30 dies de garantia i correcció d’ajustos gratuïts',
      ],
      p1Ideal: 'Restaurants, barberies, perruqueries, clíniques, assessories i autònoms locals.',
      p1Cta: 'Vull Onix Presència',
      p2Name: 'Onix Sistema',
      p2Badge: 'Eina digital i automatització',
      p2SubBadge: 'Automatització & IA',
      p2Price: 'Des de 1.500 €',
      p2Period: 'projectes a mida · suport tècnic dedicat',
      p2Desc: 'Una solució a mida per a negocis que necessiten gestionar contactes, reserves, pressupostos o tasques internes automàticament.',
      p2FeaturesTitle: 'Funcionalitats que pot incloure:',
      p2Features: [
        'Web o aplicació digital completa amb funcionalitats a mida',
        'Panell privat d’administració per al teu negoci',
        'Sistema intel·ligent de reserves o sol·licituds de pressupost',
        'Gestió centralitzada de contactes i clients',
        'Automatitzacions i notificacions per email i WhatsApp',
        'Integració amb calendari, pagaments o CRM',
        'Base de dades segura i estadístiques clares de rendiment',
        'Manteniment i suport tècnic continuat per a la màxima seguretat',
      ],
      p2Ideal: 'Empreses amb volum de reserves, sol·licituds diàries de pressupost o processos repetitius.',
      p2Cta: 'Vull Onix Sistema',
    },
    roiCalculator: {
      badge: 'Retorn de la inversió (ROI)',
      title: 'Calcula quant et costa realment la teua web',
      sub: 'Una web professional no s’ha de valorar pel seu preu, sinó pel nombre de clients que t’ajuda a aconseguir.',
      ticketLabel: 'Preu mitjà del teu servei o tiquet mitjà (€):',
      clientsLabel: 'Clients nous que podries guanyar al mes amb una web visible:',
      clientsPerMonth: 'clients/mes',
      solutionLabel: 'Tria la solució:',
      solutionP1: 'Onix Presència (des de 390 €)',
      solutionP2: 'Onix Sistema (des de 1.500 €)',
      resultTitle: 'Retorn estimat de la teua inversió',
      clientsNeededText: 'Clients necessaris per pagar tota la web:',
      clientsNeededPrefix: 'Només',
      clientsNeededSuffix: 'clients nous!',
      clientsExplanation: 'Amb aquests clients ja hauràs amortitzat completament la inversió de la solució.',
      monthlyExtraText: 'Facturació addicional mensual estimada:',
      paybackTimeText: 'Temps estimat per recuperar la inversió:',
      monthUnitSingular: 'mes',
      monthUnitPlural: 'mesos',
      sixMonthsText: 'Retorn estimat a 6 mesos:',
      twelveMonthsText: 'Retorn estimat a 12 mesos:',
      disclaimer: 'Aquest càlcul és una estimació orientativa i no una promesa de resultats. El retorn depèn del sector, la demanda, la qualitat del servei i la rapidesa de resposta del negoci.',
      ctaWhatsApp: 'Consultar per WhatsApp',
    },
    projects: {
      badge: 'Sectors i Demos',
      title: 'Solucions pensades per al teu sector',
      sub: 'Pots obrir directament cadascuna de les nostres demos reals i comprovar com funcionen en viu.',
      allDemosBanner: 'Tenim desenes de demos reals creades per a diferents sectors.',
      allDemosButton: 'Explorar tot el catàleg a demo.onixdigital.pro ↗',
      openDemoButton: 'Obrir demo en directe ↗',
      askCustomButton: 'Demanar pressupost →',
      keyHighlight: 'Punt fort del sistema:',
      categories: [
        { id: 'all', label: 'Tots els sectors' },
        { id: 'restauracio', label: 'Hostaleria & Cartes QR' },
        { id: 'bellesa', label: 'Bellesa & Comerç' },
        { id: 'serveis', label: 'Serveis, Legal & Oci' },
      ],
      
      
      items: [
        {
          category: 'Servicios, Legal & Ocio',
          sectorId: 'serveis',
          title: 'JLC Advocats',
          description: 'Web corporativa para despacho de abogados con gestión de contenidos y diseño profesional.',
          tags: ['Legal', 'Corporatiu', 'Wordpress'],
          highlight: 'Diseño sobrio y profesional para servicios legales',
          demoUrl: 'https://jlcadvocats.org/',
        },
        {
          category: 'Belleza & Comercio',
          sectorId: 'bellesa',
          title: 'La Nati',
          description: 'Tienda online y web corporativa para centro de belleza.',
          tags: ['Ecommerce', 'Bellesa', 'Reserves'],
          highlight: 'Catálogo de productos e imagen de marca cuidada',
          demoUrl: 'https://www.nati.cat/',
        },
        {
          category: 'Belleza & Comercio',
          sectorId: 'bellesa',
          title: 'Caseta Lilla',
          description: 'Proyecto de comercio y aplicación con CRM completo.',
          tags: ['CRM', 'Automatització', 'Comerç'],
          highlight: 'Gestión completa de clientes y publicaciones automatizadas',
          demoUrl: 'https://casetalilla.cat/',
        },
        {
          category: 'Servicios, Legal & Ocio',
          sectorId: 'serveis',
          title: 'Ebrecard',
          description: 'Plataforma web para servicios con CRM y funcionalidades a medida.',
          tags: ['CRM', 'Aplicació', 'Serveis'],
          highlight: 'Portal completo para usuarios y gestión avanzada',
          demoUrl: 'https://ebrecard.cat/',
        },
        {
          category: 'Servicios, Legal & Ocio',
          sectorId: 'serveis',
          title: 'Dentalmar',
          description: 'Clínica dental con reservas e información de servicios y tratamientos.',
          tags: ['Salut', 'Clínica', 'Corporatiu'],
          highlight: 'Diseño orientado a la confianza y captación de pacientes',
          demoUrl: 'https://demo.onixdigital.pro/Dentalmar-web/',
        },
        {
          category: 'Servicios, Legal & Ocio',
          sectorId: 'serveis',
          title: 'Centre Fran Severi',
          description: 'Centro de servicios con información detallada y portales de clientes.',
          tags: ['Serveis', 'Corporatiu', 'CRM'],
          highlight: 'Interfaz limpia para la presentación de servicios',
          demoUrl: 'https://demo.onixdigital.pro/NO-centreFranSeveri',
        },
        {
          category: 'Hostelería & Cartas QR',
          sectorId: 'restauracio',
          title: 'La Borda d\'en Manel',
          description: 'Restaurante con carta digital, menús diarios y reservas.',
          tags: ['Restaurant', 'Carta QR', 'Reserves'],
          highlight: 'Integración perfecta para reservas y menú dinámico',
          demoUrl: 'https://demo.onixdigital.pro/labordadenmanel/',
        },
        {
          category: 'Servicios, Legal & Ocio',
          sectorId: 'serveis',
          title: 'Impremta Delta',
          description: 'Web corporativa para servicios de impresión.',
          tags: ['Impremta', 'Corporatiu', 'Serveis'],
          highlight: 'Muestra de portfolio y contacto directo',
          demoUrl: 'https://demo.onixdigital.pro/impremtaDelta/',
        },
        {
          category: 'Hostelería & Cartas QR',
          sectorId: 'restauracio',
          title: 'Braseria Karibera',
          description: 'Restaurante asador con sección de galería y reservas integradas.',
          tags: ['Hostaleria', 'Braseria', 'Fotos'],
          highlight: 'Galería visual atractiva para la captación',
          demoUrl: 'https://demo.onixdigital.pro/Braseriakaribera/',
        },
        {
          category: 'Hostelería & Cartas QR',
          sectorId: 'restauracio',
          title: 'Aromi',
          description: 'Web gastronómica para restaurante moderno.',
          tags: ['Restaurant', 'Modern', 'Carta'],
          highlight: 'Diseño exclusivo para destacar la experiencia gastronómica',
          demoUrl: 'https://demo.onixdigital.pro/aromi/',
        },
        {
          category: 'Servicios, Legal & Ocio',
          sectorId: 'serveis',
          title: 'Carles Assessors',
          description: 'Despacho de asesores y gestores para empresas y particulares.',
          tags: ['Legal', 'Assessoria', 'Corporatiu'],
          highlight: 'Claridad en servicios legales y financieros',
          demoUrl: 'https://demo.onixdigital.pro/carlesassessors/',
        },
        {
          category: 'Servicios, Legal & Ocio',
          sectorId: 'serveis',
          title: 'Advocat Diego Perez',
          description: 'Bufete de abogados especializados con formulario de contacto y áreas.',
          tags: ['Advocat', 'Legal', 'Corporatiu'],
          highlight: 'Perfil profesional para la generación de leads',
          demoUrl: 'https://demo.onixdigital.pro/advocatDiegoPerez/',
        }
      ],
    },
    addons: {
      badge: 'Ampliacions opcionals',
      title: 'Serveis complementaris per créixer',
      sub: 'Pots afegir aquestes millores des del principi o quan el teu negoci estiga preparat per fer el següent pas.',
      infoButton: 'Demanar informació',
      items: [
        {
          title: 'SEO Inicial de Posicionament',
          price: 'Des de 250 €',
          desc: 'Configuració professional per ser trobat a la teua ciutat i al teu sector.',
          points: [
            'Estudi de paraules clau locals del teu negoci',
            'Optimització de Google Business Profile (Google Maps)',
            'Títols, metadades i textos orientats al teu municipi',
            'Connexió amb Google Search Console i Analytics',
          ],
        },
        {
          title: 'SEO Local Mensual',
          price: 'Des de 249 € / mes',
          desc: 'Treball continuat per guanyar visibilitat i posicions a la teua zona.',
          points: [
            'Revisió mensual de rànquing a Google i Maps',
            'Publicacions i actualitzacions de Google Business',
            'Optimització de textos i continguts de la web',
            'Informe senzill i entenedor d’evolució',
          ],
        },
        {
          title: 'Cartes Digitals Interactives',
          price: 'Des de 120 €',
          desc: 'Menú digital ràpid, net i adaptat a mòbil per a bars i restaurants.',
          points: [
            'Càrrega ultraràpida sense dependre de PDFs pesats',
            'Fàcil d’actualitzar plats, preus i al·lèrgens',
            'Generació de codis QR per a les taules',
          ],
        },
        {
          title: 'Integració WhatsApp & IA',
          price: 'Des de 290 €',
          desc: 'Automatitza les respostes freqüents i no perdes cap consulta fora d’horari.',
          points: [
            'Respostes automàtiques a horaris, preus i ubicació',
            'Derivació de consultes urgents al teu mòbil',
            'Captació de dades del client per al seguiment',
          ],
        },
        {
          title: 'Manteniment i Suport Opcional',
          price: 'Des de 19 € / mes',
          desc: 'Per a la solució Onix Presència (a la landing és 100% opcional).',
          points: [
            'Allotjament d’alta velocitat i certificat SSL segur',
            'Còpies de seguretat periòdiques i supervisió',
            'Canvis menors de text o fotos sense complicacions',
          ],
        },
      ],
    },
    process: {
      badge: 'Com treballem',
      title: 'Un procés simple en 4 passos',
      sub: 'Sense perdre el temps en reunions infinites ni tecnicismes innecessaris.',
      steps: [
        {
          num: '01',
          title: 'Ens expliques el teu negoci',
          desc: 'Una conversa breu per entendre què ofereixes, quin tipus de clients busques i quina informació necessiten.',
        },
        {
          num: '02',
          title: 'Tria de la solució adecuada',
          desc: 'Et recomanem amb honestedat si et convé començar amb Onix Presència o si necessites Onix Sistema.',
        },
        {
          num: '03',
          title: 'Creem i publiquem la web',
          desc: 'Dissenyem, redactem els textos comercials, configurem el domini i ho provem tot abans de publicar-ho.',
        },
        {
          num: '04',
          title: 'Acompanyament i millora',
          desc: 'La teua web queda en marxa. Tens suport per a dubtes i pots ampliar serveis quan vulgues.',
        },
      ],
    },
    pricing: {
      badge: 'Preus clars',
      title: 'Inversió transparent des del primer dia',
      sub: 'Sense lletra petita, sense permanències ocultes i amb la màxima claredat.',
      fromLabel: 'Des de',
      whatsIncluded: 'Què pot incloure:',
      plan1: {
        tag: 'Landing Professional',
        subTag: 'Sense permanència',
        name: 'Onix Presència',
        price: 'Des de 390 €',
        period: 'Pagament únic per disseny i publicació',
        features: [
          'Landing page adaptada 100% a mòbil',
          'Presentació de serveis, horaris i Google Maps',
          'Botó directe de WhatsApp i trucada',
          'Formulari de contacte directe',
          'SEO tècnic bàsic per a Google i IA',
          '30 dies de garantia i ajustos gratuïts',
        ],
        optionalText: 'Manteniment opcional des de 19 €/mes (no obligatori)',
        cta: 'Començar amb Onix Presència',
      },
      plan2: {
        tag: 'Eina Digital a Mida',
        subTag: 'Reserves & Processos',
        name: 'Onix Sistema',
        price: 'Des de 1.500 €',
        period: 'Solució digital completa personalitzada',
        features: [
          'Web o aplicació digital avançada a mida',
          'Panell privat per al teu equip',
          'Gestió de reserves o pressupostos intel·ligents',
          'Notificacions automàtiques per WhatsApp i email',
          'Integració amb pagaments, calendari o CRM',
          'Base de dades segura i estadístiques en temps real',
          'Suport tècnic i manteniment d’infraestructura dedicat',
        ],
        cta: 'Demanar proposta per a Onix Sistema',
      },
      noteTitle: '💡 Claritat i flexibilitat absoluta',
      noteText: 'Per a la landing Onix Presència la web és 100% de la teua propietat i no et cobrem cap manteniment obligatori. Només pagues per suport si tu decideixes contractar-lo. Per a sistemes amb bases de dades i servidors actius, el manteniment tècnic s’adapta al consum real de la teua plataforma.',
    },
    faq: {
      badge: 'Dubtes resolts',
      title: 'Preguntes freqüents',
      sub: 'Tot el que necessites saber abans de començar amb la teua nova web.',
      extraQuestionTitle: 'Tens una altra pregunta sobre el teu negoci?',
      extraQuestionText: 'Et responem ràpidament sense cap mena de compromís.',
      extraQuestionButton: 'Parlar per WhatsApp',
      items: [
        {
          q: 'És obligatori pagar manteniment cada mes?',
          a: 'No. En el cas d’Onix Presència (landing de 390 €), la web és teua i no té cap quota mensual obligatòria. Inclou 30 dies de revisió gratuïta. Només si vols que ens encarreguem de l’allotjament, seguretat i actualitzacions puntuals, pots contractar el suport opcional per 19 €/mes.',
        },
        {
          q: 'Quant de temps es triga a tenir la web publicada?',
          a: 'Per a Onix Presència, el termini habitual és d’entre 3 i 7 dies laborables un cop tenim el contingut bàsic (serveis, horaris i fotos). Per a Onix Sistema, en funció de les funcionalitats a mida, sol ser d’entre 2 i 3 setmanes.',
        },
        {
          q: 'Si ja tinc Instagram o xarxes, realment necessite una web?',
          a: 'Sí. A Instagram només et veu una fracció dels teus seguidors i no apareixes a les cerques de Google o Google Maps de persones que necessiten el teu servei ara mateix a la teua ciutat. La web és el teu espai oficial i fa que no depengues dels canvis d’algoritme.',
        },
        {
          q: 'Com em trobarà la gent a Google?',
          a: 'Construïm la web amb SEO tècnic, codi net, dades estructurades i textos optimitzats perquè Google comprengui què fas i on et trobes. Si a més vols treballar les primeres posicions de forma activa, oferim el paquet de SEO local.',
        },
        {
          q: 'Puc afegir noves funcionalitats o canvis en el futur?',
          a: 'I tant. Les nostres webs estan preparades per créixer. Pots començar amb Onix Presència i més endavant afegir un sistema de reserves, una carta digital, automatitzacions de WhatsApp o un panell privat.',
        },
      ],
    },
    contact: {
      badge: 'Comencem',
      title: 'Explica’ns què necessita el teu negoci',
      sub: 'Pots enviar-nos un missatge a través del formulari o escriure’ns directament per WhatsApp.',
      formTitle: 'Demana la teua proposta sense compromís',
      nameLabel: 'El teu nom:',
      namePlaceholder: 'Ex: Marta Soler',
      businessLabel: 'Nom del negoci o sector:',
      businessPlaceholder: 'Ex: Restaurant El Racó / Clínica dental',
      phoneLabel: 'Telèfon (WhatsApp):',
      phonePlaceholder: '+34 600 000 000',
      emailLabel: 'Email (opcional):',
      solutionLabel: 'Quina solució t’interessa?',
      solutionOptions: [
        { value: 'Onix Presència (des de 390 €)', label: 'Onix Presència (landing des de 390 €)' },
        { value: 'Onix Sistema (des de 1.500 €)', label: 'Onix Sistema (eina a mida des de 1.500 €)' },
        { value: 'SEO Local i Google Business', label: 'SEO Local i Posicionament Maps' },
        { value: 'Carta Digital Interactiva QR', label: 'Carta Digital Interactiva QR' },
        { value: 'Assessorament i Diagnòstic', label: 'No ho sé encara, necessite assessorament' },
      ],
      messageLabel: 'Explica’ns breument què necessites:',
      messagePlaceholder: 'Ex: Vull una web per a la meua perruqueria amb horaris i botó de WhatsApp...',
      submitBtn: 'Enviar sol·licitud',
      submittingBtn: 'Enviant...',
      privacyNote: 'En enviar aquest formulari acceptes la nostra política de privacitat. Responem en menys de 24 hores.',
      successTitle: 'Sol·licitud rebuda correctament!',
      successDesc: 'Moltes gràcies. Ens posarem en contacte amb tu en menys de 24 hores per a orientar-te.',
      openWhatsAppBtn: 'Parlar per WhatsApp',
      directBadge: 'Atenció directa',
      directTitle: 'Prefereixes parlar directament?',
      directDesc: 'Escriu-nos directament a WhatsApp i resoldrem els teus dubtes a l’instant.',
      whatsappBtn: 'Parlar per WhatsApp',
      callBtn: 'Trucar al +34 694 91 97 57',
      commitmentTitle: 'El nostre compromís',
      commitmentPoints: [
        'Pressupost clar i tancat des del primer dia',
        'Sense quotes obligatòries per a la teua landing',
        'Responem qualsevol consulta en menys de 24 hores',
      ],
    },
    footer: {
      tagline: 'Webs, intel·ligència artificial i sistemes digitals orientats a resultats per a negocis que volen créixer.',
      solutionsTitle: 'Solucions i Serveis',
      solutionsList: ['Onix Presència (des de 390 €)', 'Onix Sistema (des de 1.500 €)', 'SEO Local i Google Business', 'Cartes Digitals QR', 'Automatitzacions WhatsApp'],
      navTitle: 'Navegació',
      linksList: ['Solucions', 'Per què una web', 'Calculadora ROI', 'Preus', 'Contacte'],
      contactBadge: 'Contacte directe',
      phoneLabel: 'Telèfon:',
      rights: '© 2026 Onix Digital. Tots els drets reservats.',
      legal: ['Política de Privacitat', 'Avís Legal', 'Política de Cookies'],
    },
    whatsapp: {
      tooltip: 'Parlar per WhatsApp',
      message: 'Hola! M’agradaria informació sobre les solucions digitals d’Onix Digital per al meu negoci.',
    },
    marquee: ['Onix Presència des de 390 €', 'Onix Sistema des de 1.500 €', 'Webs per a negocis locals', 'Sense quotes obligatòries', 'Optimització per a Google i IA'],
    stats: {
      badge: 'En xifres',
      deliveryLabel: 'Temps de lliurament',
      deliverySub: 'Des de briefing fins a web en producció',
      projectsLabel: 'Projectes completats',
      projectsSub: 'Webs i sistemes digitals',
      automationsLabel: 'Automatitzacions actives',
      automationsSub: 'Reserves i consultes automàtiques',
      uptimeLabel: 'Disponibilitat',
      uptimeSub: 'Uptime garantit 24/7',
    },
    testimonials: {
      quote: 'Una web pròpia ens ha permès deixar de dependre exclusivament d’Instagram i rebre reserves cada dia.',
      name: 'Carles Miralles',
      role: 'Propietari de negoci local',
      metric: '+180% consultes',
    },
  },
  es: {
    header: {
      solutions: 'Soluciones',
      whyWeb: 'Por qué una web',
      roi: 'Calculadora ROI',
      projects: 'Demos por sectores',
      pricing: 'Precios',
      contact: 'Contacto',
      cta: 'Quiero mi web',
    },
    hero: {
      badge: 'Web + IA + Automatización para negocios que quieren crecer',
      headlinePart1: 'Tu presencia digital comienza con un',
      headlineHighlight: 'espacio propio.',
      sub: 'Creamos webs y sistemas digitales para que los clientes te encuentren, entiendan qué ofreces y puedan contactarte sin complicaciones.',
      ctaPrimary: 'Quiero mejorar la presencia de mi negocio',
      ctaSecondary: 'Ver las soluciones',
      pill1: 'Sin cuotas obligatorias',
      pill2: 'Entrega rápida y optimizada',
      pill3: 'Optimizado para Google e IA',
      mockup: {
        category: 'Restaurante & Café Local',
        status: '● Abierto ahora',
        title: 'Cocina de Mercado & Producto Fresco',
        description: 'Menú del día, carta digital interactiva y reservas directas por WhatsApp.',
        ctaPrimary: '💬 WhatsApp',
        ctaSecondary: 'Ver carta',
        reviewsText: '(148 reseñas en Google)',
        locationText: 'Calle Mayor, 12 · Valencia',
        aiTitle: 'Optimizado para nuevos buscadores',
        aiText: 'ChatGPT, Perplexity y Gemini comprenden tus horarios y servicios.',
        floatingTag: 'Dominio Propio Incluido',
        floatingPrice: 'Desde 390 € · Pago único',
      },
    },
    socialVsWeb: {
      badge: 'El problema de no tener web propia',
      title: '¿Redes sociales o espacio propio?',
      sub: 'Muchos negocios dependen únicamente de Instagram, TikTok o WhatsApp. Estas plataformas ayudan, pero no son tu terreno propio.',
      socialTag: 'La realidad actual',
      socialTitle: 'Depender solo de redes sociales',
      socialSubtitle: 'Tu visibilidad depende de algoritmos y reglas ajenas',
      socialPoints: [
        'Dependes al 100% de cambios continuos de algoritmo.',
        'Menos del 10% de tus seguidores ven lo que publicas.',
        'La información clave (servicios, horarios, dirección) queda enterrada.',
        'Si no conocen tu nombre exacto en Instagram, no te encuentran en Google.',
        'La plataforma puede cerrar tu cuenta o cambiar las normas de un día para otro.',
      ],
      socialQuote: '«Si solo estás en Instagram, dependes de un algoritmo que puede reducir tu alcance en cualquier momento.»',
      webTag: 'El activo propio',
      webTitle: 'Tener tu propia web con Onix',
      webSubtitle: 'Un activo digital sólido que trabaja para ti 24/7',
      webPoints: [
        'Es tu espacio oficial: nadie puede cambiarte las reglas.',
        'Toda la información ordenada: fotos, servicios, carta, horarios y opiniones.',
        'Aparece en Google Maps y búsquedas locales cuando buscan tu servicio.',
        'Estructura clara para que los nuevos buscadores de IA entiendan tu negocio.',
        'Centraliza contactos, llamadas y mensajes de WhatsApp en un clic.',
      ],
      webQuote: '★ Un lugar oficial que aporta seriedad, centraliza contactos y convierte visitas en llamadas y citas.',
    },
    benefits: {
      badge: 'Valor real',
      title: 'Qué puede hacer una web por tu negocio',
      sub: 'Una web no es un gasto decorativo: es la herramienta que hace que los clientes te conozcan, confíen y decidan contactarte.',
      items: [
        {
          title: 'Más fácil de encontrar',
          desc: 'Los clientes que buscan tu servicio en Google o Maps encuentran tu negocio antes que a la competencia.',
          icon: 'MagnifyingGlassIcon',
        },
        {
          title: 'Explica qué ofreces en 5 segundos',
          desc: 'Sin que el visitante tenga que investigar historias destacadas: servicios, precios y fotos claras al instante.',
          icon: 'CheckBadgeIcon',
        },
        {
          title: 'Transmite confianza profesional',
          desc: 'Un negocio con dominio propio y diseño impecable genera seguridad inmediata en un cliente nuevo.',
          icon: 'ShieldCheckIcon',
        },
        {
          title: 'Centraliza todo tu contenido',
          desc: 'Horarios actualizados, ubicación interactiva, preguntas frecuentes, opiniones reales y catálogo en un solo punto.',
          icon: 'Squares2X2Icon',
        },
        {
          title: 'Contacto en un solo clic',
          desc: 'Botón directo de WhatsApp, llamada directa y formulario sencillo para eliminar fricciones.',
          icon: 'ChatBubbleLeftRightIcon',
        },
        {
          title: 'Listo para buscadores de IA',
          desc: 'Datos estructurados para que ChatGPT, Gemini y los nuevos asistentes recomienden tu negocio.',
          icon: 'SparklesIcon',
        },
      ],
    },
    solutions: {
      badge: 'Soluciones adaptadas',
      title: 'Dos formas de hacer crecer tu negocio',
      sub: 'Desde una presencia impecable para ser visible, hasta una herramienta a medida para automatizar reservas y procesos.',
      fromLabel: 'Desde',
      whatsIncluded: 'Qué puede incluir:',
      recommendedFor: 'Recomendado para:',
      p1Name: 'Onix Presencia',
      p1Badge: 'Tu punto de partida digital',
      p1SubBadge: 'Sin cuota mensual',
      p1Price: 'Desde 390 €',
      p1Period: 'pago único · sin cuotas obligatorias',
      p1Desc: 'Una landing page de alto impacto diseñada para negocios que necesitan una presencia clara, atractiva y fácil de encontrar.',
      p1Features: [
        'Diseño profesional de una página adaptada 100% a móvil',
        'Presentación del negocio, equipo y servicios destacados',
        'Horarios, dirección, teléfono y Google Maps interactivo',
        'Botones de llamada directa y WhatsApp en un clic',
        'Formulario de contacto rápido conectado a tu email',
        'Galería de fotos de alta calidad y enlaces a redes',
        'SEO técnico básico para Google y asistentes de IA',
        'Publicación con dominio propio y certificado SSL seguro',
        '30 días de garantía y ajustes incluidos',
      ],
      p1Ideal: 'Restaurantes, barberías, peluquerías, clínicas, asesorías y profesionales locales.',
      p1Cta: 'Quiero Onix Presencia',
      p2Name: 'Onix Sistema',
      p2Badge: 'Herramienta digital y automatización',
      p2SubBadge: 'Automatización & IA',
      p2Price: 'Desde 1.500 €',
      p2Period: 'proyectos a medida · soporte técnico dedicado',
      p2Desc: 'Una solución a medida para negocios que necesitan gestionar contactos, reservas, presupuestos o tareas internas automáticamente.',
      p2FeaturesTitle: 'Funcionalidades que puede incluir:',
      p2Features: [
        'Web o aplicación digital completa con funciones personalizadas',
        'Panel privado de administración para tu negocio',
        'Sistema inteligente de reservas o solicitudes de presupuesto',
        'Gestión centralizada de contactos y clientes',
        'Automatizaciones y notificaciones por email y WhatsApp',
        'Integración con calendario, pagos o CRM',
        'Base de datos segura y métricas de rendimiento',
        'Mantenimiento y soporte técnico continuado para máxima tranquilidad',
      ],
      p2Ideal: 'Empresas con alto volumen de reservas, solicitudes de presupuesto o procesos repetitivos.',
      p2Cta: 'Quiero Onix Sistema',
    },
    roiCalculator: {
      badge: 'Retorno de la inversión (ROI)',
      title: 'Calcula cuánto te cuesta realmente tu web',
      sub: 'Una web profesional no se valora por su precio, sino por el número de clientes que te ayuda a conseguir.',
      ticketLabel: 'Precio medio de tu servicio o compra media (€):',
      clientsLabel: 'Nuevos clientes que podrías captar al mes con una web visible:',
      clientsPerMonth: 'clientes/mes',
      solutionLabel: 'Elige la solución:',
      solutionP1: 'Onix Presencia (desde 390 €)',
      solutionP2: 'Onix Sistema (des de 1.500 €)',
      resultTitle: 'Retorno estimado de tu inversión',
      clientsNeededText: 'Clientes necesarios para amortizar toda la web:',
      clientsNeededPrefix: '¡Solo',
      clientsNeededSuffix: 'clientes nuevos!',
      clientsExplanation: 'Con estos clientes ya habrás amortizado completamente la inversión de la solución.',
      monthlyExtraText: 'Facturación adicional mensual estimada:',
      paybackTimeText: 'Tiempo estimado para recuperar la inversión:',
      monthUnitSingular: 'mes',
      monthUnitPlural: 'meses',
      sixMonthsText: 'Retorno estimado a 6 meses:',
      twelveMonthsText: 'Retorno estimado a 12 meses:',
      disclaimer: 'Este cálculo es una estimación orientativa y no una promesa de resultados. El retorno depende del sector, la demanda, la calidad del servicio y la rapidez de respuesta del negocio.',
      ctaWhatsApp: 'Consultar por WhatsApp',
    },
    projects: {
      badge: 'Sectores y Demos',
      title: 'Soluciones pensadas para tu sector',
      sub: 'Puedes abrir directamente cada una de nuestras demos reales y comprobar cómo funcionan en vivo.',
      allDemosBanner: 'Tenemos decenas de demos reales creadas para diversos sectores.',
      allDemosButton: 'Explorar todo el catálogo en demo.onixdigital.pro ↗',
      openDemoButton: 'Abrir demo en directo ↗',
      askCustomButton: 'Pedir presupuesto →',
      keyHighlight: 'Punto fuerte del sistema:',
      categories: [
        { id: 'all', label: 'Todos los sectores' },
        { id: 'restauracio', label: 'Hostelería & Cartas QR' },
        { id: 'bellesa', label: 'Belleza & Comercio' },
        { id: 'serveis', label: 'Servicios, Legal & Ocio' },
      ],
      items: [
        {
          category: 'Hostelería & Cartas QR',
          sectorId: 'restauracio',
          title: 'Restaurants Onix',
          description: 'Plataforma completa de demostración para restauración con menú dinámico, fotografías de platos y reservas en línea.',
          tags: ['Restauración', 'Reservas WhatsApp', 'Google Maps'],
          highlight: 'Centralización de reservas y menú digital sin fricciones',
          demoUrl: 'https://restaurants.onixdigital.pro/',
        },
        {
          category: 'Hostelería & Cartas QR',
          sectorId: 'restauracio',
          title: 'Carta Digital Aromi',
          description: 'Carta digital interactiva QR para cafetería y restaurante con categorías claras, alérgenos y carga instantánea.',
          tags: ['Carta QR', 'Carga ultrarrápida', 'Mobile-first'],
          highlight: 'Actualización de carta sin necesidad de reimprimir papel',
          demoUrl: 'https://aromi-montroig-carta.miriamlc.chatgpt.site/',
        },
        {
          category: 'Hostelería & Cartas QR',
          sectorId: 'restauracio',
          title: 'La Pastisseria de Nerea',
          description: 'Presencia dulce y visual para pastelerías y obradores artesanos con catálogo de productos y pedidos directos.',
          tags: ['Pastelería artesana', 'Catálogo visual', 'Pedidos rápidos'],
          highlight: 'Facilita encargos de celebraciones y tartas en 1 clic',
          demoUrl: 'https://la-pastisseria-de-nerea-builder-kiusibdmb4zjteks.hostingersite.com/',
        },
        {
          category: 'Hostelería & Cartas QR',
          sectorId: 'restauracio',
          title: 'Bar Nocturnal',
          description: 'Web nocturna para coctelerías y bares con carta de copas, eventos y mapa interactivo.',
          tags: ['Bar de copas', 'Carta de cócteles', 'Eventos'],
          highlight: 'Estética visual oscura y atracción de público joven',
          demoUrl: 'https://bar-nocturnal-builder-cfvndgbufwnmvauz.hostingersite.com/',
        },
        {
          category: 'Belleza & Comercio',
          sectorId: 'bellesa',
          title: 'Barberia JR',
          description: 'Web moderna para barberías y peluquerías masculinas con tarifas de cortes, fotos y enlace directo a agenda.',
          tags: ['Barbería', 'Precios claros', 'Agenda de citas'],
          highlight: 'Ahorra tiempo al teléfono para consultar precios y horarios',
          demoUrl: 'https://barberia-jr-builder-nhdv4zgytxnw1qkt.hostingersite.com/',
        },
        {
          category: 'Belleza & Comercio',
          sectorId: 'bellesa',
          title: 'Dolça Aura',
          description: 'Landing elegante para centros de estética, manicura y bienestar con explicación detallada de tratamientos.',
          tags: ['Estética', 'Tratamientos', 'Confianza'],
          highlight: 'Imagen profesional que atrae a nuevos clientes locales',
          demoUrl: 'https://dolcaura-builder-qomrykimxqolbeju.hostingersite.com/',
        },
        {
          category: 'Servicios, Legal & Ocio',
          sectorId: 'serveis',
          title: 'JLC Advocats',
          description: 'Web corporativa sólida para despachos legales y asesorías con áreas de práctica y formulario cualificado.',
          tags: ['Despacho legal', 'Autoridad de marca', 'Formulario cualificado'],
          highlight: 'Transmite la solvencia y rigor que requiere un servicio profesional',
          demoUrl: 'https://jlcadvocats.org/',
        },
        {
          category: 'Servicios, Legal & Ocio',
          sectorId: 'serveis',
          title: 'Driada Disseny',
          description: 'Presentación corporativa para estudios de diseño y proyectos de interiorismo con porfolio destacado.',
          tags: ['Diseño & Interiorismo', 'Porfolio visual', 'Alta gama'],
          highlight: 'El cliente percibe el estilo y la calidad del trabajo antes de llamar',
          demoUrl: 'https://driadadisseny.com/',
        },
        {
          category: 'Servicios, Legal & Ocio',
          sectorId: 'serveis',
          title: 'Mitics Club',
          description: 'Web para salas de ocio, discotecas y eventos con agenda de fiestas, venta de entradas y reservados VIP.',
          tags: ['Ocio nocturno', 'Entradas & VIP', 'Eventos'],
          highlight: 'Información de eventos y reserva de mesas en segundos',
          demoUrl: 'https://mitics-club-builder-cbyzxeynd9bdso2l.hostingersite.com/',
        },
      ],
    },
    addons: {
      badge: 'Ampliaciones opcionales',
      title: 'Servicios complementarios para crecer',
      sub: 'Puedes añadir estas mejoras desde el inicio o cuando tu negocio esté listo para dar el siguiente paso.',
      infoButton: 'Pedir información',
      items: [
        {
          title: 'SEO Inicial de Posicionamiento',
          price: 'Desde 250 €',
          desc: 'Configuración profesional para que te encuentren en tu ciudad y sector.',
          points: [
            'Estudio de palabras clave locales de tu negocio',
            'Optimización de Google Business Profile (Google Maps)',
            'Títulos, metadatos y textos orientados a tu localidad',
            'Conexión con Google Search Console y Analytics',
          ],
        },
        {
          title: 'SEO Local Mensual',
          price: 'Des de 249 € / mes',
          desc: 'Trabajo continuado para ganar visibilidad y puestos en tu zona.',
          points: [
            'Revisión mensual de posiciones en Google y Maps',
            'Publicaciones y actualizaciones en Google Business',
            'Optimización continuada de textos y páginas',
            'Informe mensual claro de evolución',
          ],
        },
        {
          title: 'Cartas Digitales Interactivas',
          price: 'Desde 120 €',
          desc: 'Menú digital rápido, visual y adaptado a móvil para hostelería.',
          points: [
            'Carga ultrarrápida sin depender de PDFs pesados',
            'Fácil de actualizar platos, precios y alérgenos',
            'Generación de códigos QR para las mesas',
          ],
        },
        {
          title: 'Integración WhatsApp & IA',
          price: 'Desde 290 €',
          desc: 'Automatiza respuestas frecuentes y no pierdas consultas fuera de horario.',
          points: [
            'Respuestas automáticas a horarios, precios y ubicación',
            'Derivación de consultas prioritarias a tu teléfono',
            'Captación ordenada de datos del cliente',
          ],
        },
        {
          title: 'Mantenimiento y Soporte Opcional',
          price: 'Desde 19 € / mes',
          desc: 'Para Onix Presencia (en landings es 100% opcional).',
          points: [
            'Alojamiento de alta velocidad y certificado SSL seguro',
            'Copias de seguridad periódicas y supervisión',
            'Pequeños cambios de textos o fotos sin coste extra',
          ],
        },
      ],
    },
    process: {
      badge: 'Cómo trabajamos',
      title: 'Un proceso simple en 4 pasos',
      sub: 'Sin reuniones interminables ni tecnicismos innecesarios.',
      steps: [
        {
          num: '01',
          title: 'Nos explicas tu negocio',
          desc: 'Una breve conversación para entender tus servicios, tus clientes y qué información necesitan.',
        },
        {
          num: '02',
          title: 'Elegimos la solución ideal',
          desc: 'Te recomendamos con honestidad si te conviene empezar con Onix Presencia o necesitas Onix Sistema.',
        },
        {
          num: '03',
          title: 'Diseñamos y publicamos',
          desc: 'Creamos el diseño, redactamos los textos comerciales, configuramos el dominio y lo probamos todo.',
        },
        {
          num: '04',
          title: 'Acompañamiento continuo',
          desc: 'Tu web queda en marcha. Cuentas con soporte para cualquier duda y opciones de ampliación.',
        },
      ],
    },
    pricing: {
      badge: 'Precios claros',
      title: 'Inversión transparente desde el primer día',
      sub: 'Sin letra pequeña, sin permanencias ocultas y con la máxima claridad.',
      fromLabel: 'Desde',
      whatsIncluded: 'Qué puede incluir:',
      plan1: {
        tag: 'Landing Profesional',
        subTag: 'Sin permanencia',
        name: 'Onix Presencia',
        price: 'Desde 390 €',
        period: 'Pago único por diseño y publicación',
        features: [
          'Landing page adaptada 100% a móvil',
          'Presentación de servicios, horarios y Google Maps',
          'Botón directo de WhatsApp y llamada',
          'Formulario de contacto directo',
          'SEO técnico básico para Google e IA',
          '30 días de garantía y ajustes incluidos',
        ],
        optionalText: 'Mantenimiento opcional desde 19 €/mes (no obligatorio)',
        cta: 'Comenzar con Onix Presencia',
      },
      plan2: {
        tag: 'Herramienta Digital a Medida',
        subTag: 'Reservas & Procesos',
        name: 'Onix Sistema',
        price: 'Des de 1.500 €',
        period: 'Solución digital completa personalizada',
        features: [
          'Web o aplicación digital avanzada a medida',
          'Panel privado para tu equipo',
          'Gestión de reservas o presupuestos inteligentes',
          'Notificaciones automáticas por WhatsApp y email',
          'Integración con pagos, calendario o CRM',
          'Base de datos segura y métricas en tiempo real',
          'Soporte técnico y mantenimiento de infraestructura dedicado',
        ],
        cta: 'Pedir propuesta para Onix Sistema',
      },
      noteTitle: '💡 Claridad y flexibilidad absoluta',
      noteText: 'Para la landing Onix Presencia la web es 100% de tu propiedad y no cobramos ningún mantenimiento obligatorio. Solo pagas soporte si tú decides contratarlo. Para sistemas con bases de datos y servidores activos, el mantenimiento técnico se adapta al consumo real de tu plataforma.',
    },
    faq: {
      badge: 'Dudas resueltas',
      title: 'Preguntas frecuentes',
      sub: 'Todo lo que necesitas saber antes de empezar tu nueva web.',
      extraQuestionTitle: '¿Tienes otra pregunta sobre tu negocio?',
      extraQuestionText: 'Te respondemos rápidamente sin ningún tipo de compromiso.',
      extraQuestionButton: 'Hablar por WhatsApp',
      items: [
        {
          q: '¿Es obligatorio pagar mantenimiento cada mes?',
          a: 'No. En Onix Presencia (landing de 390 €), la web es tuya y no requiere cuota obligatoria. Incluye 30 días de ajustes gratuitos. Si deseas que nos encarguemos del hosting, seguridad y actualizaciones, ofrecemos soporte opcional por 19 €/mes.',
        },
        {
          q: '¿Cuánto tiempo se tarda en tener la web lista?',
          a: 'Para Onix Presencia, el plazo habitual es de 3 a 7 días laborables tras disponer de la información básica. Para Onix Sistema, dependiendo de las integraciones a medida, suele ser de 2 a 3 semanas.',
        },
        {
          q: 'Si ya tengo Instagram o redes, ¿de verdad necesito una web?',
          a: 'Sí. En redes sociales solo te ve un porcentaje reducido de tus seguidores y no apareces en las búsquedas de Google o Maps cuando alguien necesita tu servicio de inmediato en tu ciudad. Tu web es tu activo propio e independiente.',
        },
        {
          q: '¿Cómo me encontrará la gente en Google?',
          a: 'Desarrollamos la web con SEO técnico, código limpio y datos estructurados para que Google entienda con claridad qué ofreces y dónde estás ubicado. Si quieres trabajar los primeros puestos activamente, disponemos del servicio de SEO local.',
        },
        {
          q: '¿Puedo añadir más funciones en el futuro?',
          a: 'Por supuesto. Todas nuestras soluciones están preparadas para escalar. Puedes empezar con Onix Presencia y más adelante integrar un sistema de reservas, carta digital, automatizaciones de WhatsApp o un panel privado.',
        },
      ],
    },
    contact: {
      badge: 'Empezamos',
      title: 'Cuéntanos qué necesita tu negocio',
      sub: 'Puedes enviarnos tu consulta por el formulario o escribirnos directamente a WhatsApp.',
      formTitle: 'Solicita tu propuesta sin compromiso',
      nameLabel: 'Tu nombre:',
      namePlaceholder: 'Ej: Carlos Gómez',
      businessLabel: 'Nombre del negocio o sector:',
      businessPlaceholder: 'Ej: Restaurante El Faro / Clínica dental',
      phoneLabel: 'Teléfono (WhatsApp):',
      phonePlaceholder: '+34 600 000 000',
      emailLabel: 'Email (opcional):',
      solutionLabel: '¿Qué solución te interesa?',
      solutionOptions: [
        { value: 'Onix Presència (des de 390 €)', label: 'Onix Presencia (landing desde 390 €)' },
        { value: 'Onix Sistema (des de 1.500 €)', label: 'Onix Sistema (herramienta a medida desde 1.500 €)' },
        { value: 'SEO Local i Google Business', label: 'SEO Local y Posicionamiento Maps' },
        { value: 'Carta Digital Interactiva QR', label: 'Carta Digital Interactiva QR' },
        { value: 'Assessorament i Diagnòstic', label: 'No lo sé todavía, necesito asesoramiento' },
      ],
      messageLabel: 'Cuéntanos brevemente qué necesitas:',
      messagePlaceholder: 'Ej: Quiero una web para mi negocio con horarios y botón directo de WhatsApp...',
      submitBtn: 'Enviar solicitud',
      submittingBtn: 'Enviando...',
      privacyNote: 'Al enviar este formulario aceptas nuestra política de privacidad. Respondemos en menos de 24 horas.',
      successTitle: '¡Solicitud recibida correctamente!',
      successDesc: 'Muchas gracias. Nos pondremos en contacto contigo en menos de 24 horas para asesorarte.',
      openWhatsAppBtn: 'Hablar por WhatsApp',
      directBadge: 'Atención directa',
      directTitle: '¿Prefieres hablar directamente?',
      directDesc: 'Escríbenos directamente por WhatsApp y resolveremos tus dudas al instante.',
      whatsappBtn: 'Hablar por WhatsApp',
      callBtn: 'Llamar al +34 694 91 97 57',
      commitmentTitle: 'Nuestro compromiso',
      commitmentPoints: [
        'Presupuesto claro y cerrado desde el primer día',
        'Sin cuotas obligatorias para tu landing page',
        'Respondemos cualquier consulta en menos de 24 horas',
      ],
    },
    footer: {
      tagline: 'Webs, inteligencia artificial y sistemas digitales orientados a resultados para negocios que quieren crecer.',
      solutionsTitle: 'Soluciones y Servicios',
      solutionsList: ['Onix Presencia (desde 390 €)', 'Onix Sistema (des de 1.500 €)', 'SEO Local y Google Business', 'Cartas Digitales QR', 'Automatizaciones WhatsApp'],
      navTitle: 'Navegación',
      linksList: ['Soluciones', 'Por qué una web', 'Calculadora ROI', 'Precios', 'Contacto'],
      contactBadge: 'Contacto directo',
      phoneLabel: 'Teléfono:',
      rights: '© 2026 Onix Digital. Todos los derechos reservados.',
      legal: ['Política de Privacidad', 'Aviso Legal', 'Política de Cookies'],
    },
    whatsapp: {
      tooltip: 'Hablar por WhatsApp',
      message: 'Hola, me gustaría información sobre las soluciones digitales de Onix Digital para mi negocio.',
    },
    marquee: ['Onix Presencia desde 390 €', 'Onix Sistema desde 1.500 €', 'Webs para negocios locales', 'Sin cuotas obligatorias', 'Optimizado para Google e IA'],
    stats: {
      badge: 'En cifras',
      deliveryLabel: 'Tiempo de entrega',
      deliverySub: 'Desde briefing hasta web en producción',
      projectsLabel: 'Proyectos completados',
      projectsSub: 'Webs y sistemas digitales',
      automationsLabel: 'Automatizaciones activas',
      automationsSub: 'Reservas y consultas automáticas',
      uptimeLabel: 'Disponibilidad',
      uptimeSub: 'Uptime garantizado 24/7',
    },
    testimonials: {
      quote: 'Tener una web propia nos ha permitido dejar de depender solo de Instagram y recibir reservas cada día.',
      name: 'Carlos Miralles',
      role: 'Propietario de negocio local',
      metric: '+180% consultas',
    },
  },
  en: {
    header: {
      solutions: 'Solutions',
      whyWeb: 'Why a website',
      roi: 'ROI Calculator',
      projects: 'Industry Demos',
      pricing: 'Pricing',
      contact: 'Contact',
      cta: 'Get your website',
    },
    hero: {
      badge: 'Web + AI + Automation for growing businesses',
      headlinePart1: 'Your digital presence starts with a',
      headlineHighlight: 'place of your own.',
      sub: 'We build websites and digital systems so customers can easily find you, understand what you offer, and contact you seamlessly.',
      ctaPrimary: 'Improve my business presence',
      ctaSecondary: 'Explore solutions',
      pill1: 'No mandatory monthly fees',
      pill2: 'Fast & high-performance delivery',
      pill3: 'Optimized for Google & AI search',
      mockup: {
        category: 'Local Cafe & Restaurant',
        status: '● Open now',
        title: 'Market Cuisine & Fresh Local Food',
        description: 'Daily specials, interactive digital menu, and instant WhatsApp bookings.',
        ctaPrimary: '💬 WhatsApp',
        ctaSecondary: 'View menu',
        reviewsText: '(148 Google reviews)',
        locationText: 'Main Street, 12 · Downtown',
        aiTitle: 'Optimized for modern AI search',
        aiText: 'ChatGPT, Perplexity, and Gemini accurately understand your hours and services.',
        floatingTag: 'Custom Domain Included',
        floatingPrice: 'From 390 € · One-time investment',
      },
    },
    socialVsWeb: {
      badge: 'The pitfall of having no website',
      title: 'Social Media vs. Your Own Website',
      sub: 'Many businesses rely solely on social apps. They help, but they are not your proprietary digital property.',
      socialTag: 'Current reality',
      socialTitle: 'Relying solely on social media',
      socialSubtitle: 'Your reach depends on third-party algorithms',
      socialPoints: [
        'You depend 100% on volatile algorithm shifts.',
        'Less than 10% of followers actually see your posts.',
        'Key details (services, hours, location) get buried.',
        'If users do not know your exact handle, they cannot find you on Google.',
        'Platforms can change rules or disable accounts overnight.',
      ],
      socialQuote: '“If you only rely on Instagram, your reach depends on algorithms that can reduce your audience anytime.”',
      webTag: 'Your own asset',
      webTitle: 'Owning a website with Onix',
      webSubtitle: 'A solid business asset that converts 24/7',
      webPoints: [
        'Your official home: no external party changes your rules.',
        'Structured information: services, hours, photos, and reviews in one place.',
        'Visible on Google Maps and local search when customers search nearby.',
        'Structured schema data ready for new AI assistants (ChatGPT, Gemini).',
        'Direct 1-click calls, WhatsApp messages, and lead capture.',
      ],
      webQuote: '★ An official home that commands trust, centralizes inquiries, and converts visitors into paying customers.',
    },
    benefits: {
      badge: 'Real Value',
      title: 'What a website does for your business',
      sub: 'A website is not an ornament: it is the engine that earns trust and turns visitors into customers.',
      items: [
        {
          title: 'Easily discoverable',
          desc: 'Local customers searching on Google find your business instead of competitors.',
          icon: 'MagnifyingGlassIcon',
        },
        {
          title: 'Clear value in 5 seconds',
          desc: 'No confusing social highlights: clear services, pricing, and pictures instantly.',
          icon: 'CheckBadgeIcon',
        },
        {
          title: 'Instant trust & credibility',
          desc: 'A custom domain and professional aesthetic create immediate customer confidence.',
          icon: 'ShieldCheckIcon',
        },
        {
          title: 'Centralized information',
          desc: 'Accurate hours, interactive location, menus, and verified reviews in one spot.',
          icon: 'Squares2X2Icon',
        },
        {
          title: 'One-click contact',
          desc: 'Frictionless WhatsApp buttons, quick phone calls, and clean inquiry forms.',
          icon: 'ChatBubbleLeftRightIcon',
        },
        {
          title: 'AI Search Engine Ready',
          desc: 'Formatted data allowing AI search engines to accurately recommend your business.',
          icon: 'SparklesIcon',
        },
      ],
    },
    solutions: {
      badge: 'Tailored Solutions',
      title: 'Two ways to grow your local business',
      sub: 'From a sharp presence to get discovered, to a custom digital system to automate bookings and inquiries.',
      fromLabel: 'From',
      whatsIncluded: 'What it can include:',
      recommendedFor: 'Recommended for:',
      p1Name: 'Onix Presence',
      p1Badge: 'Your digital launchpad',
      p1SubBadge: 'No monthly fee',
      p1Price: 'From 390 €',
      p1Period: 'one-time investment · no mandatory monthly fee',
      p1Desc: 'A high-impact landing page designed for businesses needing a clear, modern, and easily discoverable presence.',
      p1Features: [
        'Mobile-first responsive design',
        'Business overview, services, and photo showcase',
        'Operating hours, location, and Google Maps integration',
        'Direct WhatsApp and call buttons',
        'Lead capture contact form linked to your email',
        'Basic local SEO configuration for Google & AI',
        'Custom domain setup and secure SSL certificate',
        '30 days of warranty and adjustments included',
      ],
      p1Ideal: 'Restaurants, barber shops, clinics, law/tax firms, and local service providers.',
      p1Cta: 'Choose Onix Presence',
      p2Name: 'Onix System',
      p2Badge: 'Digital tool & automation',
      p2SubBadge: 'Automation & AI',
      p2Price: 'From 1,500 €',
      p2Period: 'custom projects · dedicated technical support',
      p2Desc: 'A custom software solution for businesses that need to manage inquiries, bookings, or internal operations automatically.',
      p2FeaturesTitle: 'Features it can include:',
      p2Features: [
        'Custom web application built for your workflows',
        'Private admin dashboard for team management',
        'Smart booking or quote request system',
        'Centralized lead and customer management',
        'Email and WhatsApp notifications and automations',
        'Calendar, CRM, or payment integrations',
        'Secure database and performance analytics',
        'Ongoing technical maintenance and updates',
      ],
      p2Ideal: 'Businesses with high inquiry volume, appointment bookings, or repetitive manual tasks.',
      p2Cta: 'Choose Onix System',
    },
    roiCalculator: {
      badge: 'Return on Investment (ROI)',
      title: 'Calculate the real cost of your website',
      sub: 'A website should not be evaluated by its price tag, but by the extra clients it brings to your door.',
      ticketLabel: 'Average service or order value (€):',
      clientsLabel: 'Estimated new monthly clients from an active website:',
      clientsPerMonth: 'clients/month',
      solutionLabel: 'Choose solution:',
      solutionP1: 'Onix Presence (from 390 €)',
      solutionP2: 'Onix System (from 1,500 €)',
      resultTitle: 'Estimated Investment Return',
      clientsNeededText: 'Clients needed to pay off the entire website:',
      clientsNeededPrefix: 'Only',
      clientsNeededSuffix: 'new clients!',
      clientsExplanation: 'With these new clients you will have fully recovered your investment.',
      monthlyExtraText: 'Estimated additional monthly revenue:',
      paybackTimeText: 'Estimated payback period:',
      monthUnitSingular: 'month',
      monthUnitPlural: 'months',
      sixMonthsText: 'Estimated 6-month gross return:',
      twelveMonthsText: 'Estimated 12-month gross return:',
      disclaimer: 'This calculation is an estimate and not a guarantee. Actual return depends on industry demand, service quality, and response speed.',
      ctaWhatsApp: 'Chat on WhatsApp',
    },
    projects: {
      badge: 'Industry Demos',
      title: 'Solutions crafted for your sector',
      sub: 'You can open and test each of our live production demos directly in your browser.',
      allDemosBanner: 'We have dozens of production demos built for various local industries.',
      allDemosButton: 'Browse full catalog on demo.onixdigital.pro ↗',
      openDemoButton: 'Open live demo ↗',
      askCustomButton: 'Request proposal →',
      keyHighlight: 'System highlight:',
      categories: [
        { id: 'all', label: 'All sectors' },
        { id: 'restauracio', label: 'Dining & QR Menus' },
        { id: 'bellesa', label: 'Beauty & Retail' },
        { id: 'serveis', label: 'Services, Legal & Events' },
      ],
      
      items: [
        {
          category: 'Services, Legal & Events',
          sectorId: 'serveis',
          title: 'JLC Advocats',
          description: 'Corporate website for law firm with content management and professional design.',
          tags: ['Legal', 'Corporatiu', 'Wordpress'],
          highlight: 'Sober and professional design for legal services',
          demoUrl: 'https://jlcadvocats.org/',
        },
        {
          category: 'Beauty & Retail',
          sectorId: 'bellesa',
          title: 'La Nati',
          description: 'Online store and corporate website for a beauty center.',
          tags: ['Ecommerce', 'Bellesa', 'Reserves'],
          highlight: 'Product catalog and careful brand image',
          demoUrl: 'https://www.nati.cat/',
        },
        {
          category: 'Beauty & Retail',
          sectorId: 'bellesa',
          title: 'Caseta Lilla',
          description: 'Commerce project and application with complete CRM.',
          tags: ['CRM', 'Automatització', 'Comerç'],
          highlight: 'Complete client management and automated publishing',
          demoUrl: 'https://casetalilla.cat/',
        },
        {
          category: 'Services, Legal & Events',
          sectorId: 'serveis',
          title: 'Ebrecard',
          description: 'Web platform for services with CRM and custom functionalities.',
          tags: ['CRM', 'Aplicació', 'Serveis'],
          highlight: 'Complete portal for users and advanced management',
          demoUrl: 'https://ebrecard.cat/',
        },
        {
          category: 'Services, Legal & Events',
          sectorId: 'serveis',
          title: 'Dentalmar',
          description: 'Dental clinic with reservations and information on services and treatments.',
          tags: ['Salut', 'Clínica', 'Corporatiu'],
          highlight: 'Design focused on trust and patient acquisition',
          demoUrl: 'https://demo.onixdigital.pro/Dentalmar-web/',
        },
        {
          category: 'Services, Legal & Events',
          sectorId: 'serveis',
          title: 'Centre Fran Severi',
          description: 'Service center with detailed information and client portals.',
          tags: ['Serveis', 'Corporatiu', 'CRM'],
          highlight: 'Clean interface for presenting services',
          demoUrl: 'https://demo.onixdigital.pro/NO-centreFranSeveri',
        },
        {
          category: 'Dining & QR Menus',
          sectorId: 'restauracio',
          title: 'La Borda d\'en Manel',
          description: 'Restaurant with digital menu, daily menus, and reservations.',
          tags: ['Restaurant', 'Carta QR', 'Reserves'],
          highlight: 'Seamless integration for reservations and dynamic menu',
          demoUrl: 'https://demo.onixdigital.pro/labordadenmanel/',
        },
        {
          category: 'Services, Legal & Events',
          sectorId: 'serveis',
          title: 'Impremta Delta',
          description: 'Corporate website for printing services.',
          tags: ['Impremta', 'Corporatiu', 'Serveis'],
          highlight: 'Portfolio showcase and direct contact',
          demoUrl: 'https://demo.onixdigital.pro/impremtaDelta/',
        },
        {
          category: 'Dining & QR Menus',
          sectorId: 'restauracio',
          title: 'Braseria Karibera',
          description: 'Steakhouse restaurant with gallery section and integrated reservations.',
          tags: ['Hostaleria', 'Braseria', 'Fotos'],
          highlight: 'Attractive visual gallery for acquisition',
          demoUrl: 'https://demo.onixdigital.pro/Braseriakaribera/',
        },
        {
          category: 'Dining & QR Menus',
          sectorId: 'restauracio',
          title: 'Aromi',
          description: 'Gastronomic website for a modern restaurant.',
          tags: ['Restaurant', 'Modern', 'Carta'],
          highlight: 'Exclusive design to highlight the gastronomic experience',
          demoUrl: 'https://demo.onixdigital.pro/aromi/',
        },
        {
          category: 'Services, Legal & Events',
          sectorId: 'serveis',
          title: 'Carles Assessors',
          description: 'Advisors and managers office for companies and individuals.',
          tags: ['Legal', 'Assessoria', 'Corporatiu'],
          highlight: 'Clarity in legal and financial services',
          demoUrl: 'https://demo.onixdigital.pro/carlesassessors/',
        },
        {
          category: 'Services, Legal & Events',
          sectorId: 'serveis',
          title: 'Advocat Diego Perez',
          description: 'Specialized law firm with contact form and practice areas.',
          tags: ['Advocat', 'Legal', 'Corporatiu'],
          highlight: 'Professional profile for lead generation',
          demoUrl: 'https://demo.onixdigital.pro/advocatDiegoPerez/',
        }
      ],
    },
    addons: {
      badge: 'Optional Add-ons',
      title: 'Complementary services to scale',
      sub: 'Add these capabilities immediately or whenever your business is ready for the next milestone.',
      infoButton: 'Request info',
      items: [
        {
          title: 'Initial SEO Foundation',
          price: 'From 250 €',
          desc: 'Professional setup to be discovered in your city and field.',
          points: [
            'Local keyword research for your business',
            'Google Business Profile optimization',
            'Localized metadata, headlines, and structured copy',
            'Google Search Console and Analytics setup',
          ],
        },
        {
          title: 'Monthly Local SEO',
          price: 'From 249 € / month',
          desc: 'Ongoing work to gain visibility and dominate local map pack.',
          points: [
            'Monthly ranking monitoring on Google Maps',
            'Google Business profile updates & posts',
            'Website copy and page refinement',
            'Clear monthly performance report',
          ],
        },
        {
          title: 'Interactive Digital Menus',
          price: 'From 120 €',
          desc: 'Fast, sleek mobile digital menu for restaurants and cafes.',
          points: [
            'Ultra-fast load time without bulky PDFs',
            'Simple dish and price management',
            'QR code generation for table stands',
          ],
        },
        {
          title: 'WhatsApp & AI Integration',
          price: 'From 290 €',
          desc: 'Automate FAQs and never miss an after-hours inquiry.',
          points: [
            'Instant answers on pricing, hours, and address',
            'Escalation of key customer inquiries to staff',
            'Contact data capture for follow-ups',
          ],
        },
        {
          title: 'Optional Maintenance & Hosting',
          price: 'From 19 € / month',
          desc: 'For Onix Presence (100% optional for landing pages).',
          points: [
            'High-speed hosting and SSL certificate',
            'Regular backups and security monitoring',
            'Minor text and photo updates included',
          ],
        },
      ],
    },
    process: {
      badge: 'Our Process',
      title: 'A simple 4-step workflow',
      sub: 'No endless meetings or unnecessary jargon.',
      steps: [
        {
          num: '01',
          title: 'Tell us about your business',
          desc: 'A brief discussion to understand your services, target clients, and key information.',
        },
        {
          num: '02',
          title: 'Choose the right solution',
          desc: 'We honestly suggest whether Onix Presence or Onix System fits your current stage.',
        },
        {
          num: '03',
          title: 'Design, build & launch',
          desc: 'We craft the design, write commercial copy, connect your domain, and test thoroughly.',
        },
        {
          num: '04',
          title: 'Growth & support',
          desc: 'Your website is live. We stand by for support and help you scale as needed.',
        },
      ],
    },
    pricing: {
      badge: 'Clear Pricing',
      title: 'Transparent investment from day one',
      sub: 'No fine print, no lock-in traps, just crystal-clear value.',
      fromLabel: 'From',
      whatsIncluded: 'What it can include:',
      plan1: {
        tag: 'Professional Landing',
        subTag: 'No contract lock-in',
        name: 'Onix Presence',
        price: 'From 390 €',
        period: 'One-time investment for design and publication',
        features: [
          'Mobile-first responsive design',
          'Business overview, services, and Google Maps',
          'Direct WhatsApp and call buttons',
          'Direct contact lead capture form',
          'Basic local SEO configuration for Google & AI',
          '30 days of warranty and adjustments included',
        ],
        optionalText: 'Optional support from 19 €/month (not mandatory)',
        cta: 'Start with Onix Presence',
      },
      plan2: {
        tag: 'Custom Digital Platform',
        subTag: 'Bookings & Workflows',
        name: 'Onix System',
        price: 'From 1,500 €',
        period: 'Complete tailored digital platform',
        features: [
          'Advanced custom web application built for your needs',
          'Private admin portal for your staff',
          'Smart booking or customized quote system',
          'Automated email and WhatsApp notifications',
          'Payment gateway, calendar, or CRM integration',
          'Secure database and real-time performance analytics',
          'Dedicated infrastructure maintenance and technical support',
        ],
        cta: 'Request proposal for Onix System',
      },
      noteTitle: '💡 Complete clarity and flexibility',
      noteText: 'For Onix Presence, the website is 100% your own property and we never charge mandatory monthly fees. You only pay for ongoing support if you decide to request it. For custom platforms with active databases and cloud services, technical maintenance scales with your actual infrastructure needs.',
    },
    faq: {
      badge: 'Got Questions?',
      title: 'Frequently Asked Questions',
      sub: 'Everything you need to know before launching your new website.',
      extraQuestionTitle: 'Have another question about your business?',
      extraQuestionText: 'We respond quickly with zero sales pressure.',
      extraQuestionButton: 'Chat on WhatsApp',
      items: [
        {
          q: 'Is there a mandatory monthly maintenance fee?',
          a: 'No. For Onix Presence (390 € landing page), the website is yours with no mandatory monthly cost. 30 days of adjustments are included. If you want us to handle hosting, security, and updates, optional support is available for 19 €/month.',
        },
        {
          q: 'How long does it take to launch?',
          a: 'For Onix Presence, typical delivery is 3 to 7 business days after receiving basic content. For Onix System, depending on custom workflows, it takes 2 to 3 weeks.',
        },
        {
          q: 'If I have Instagram, do I really need a website?',
          a: 'Yes. Social media algorithms only display your content to a small percentage of followers, and social profiles do not rank properly on Google Maps when high-intent local customers search for services. A website is your own independent asset.',
        },
        {
          q: 'How will people find me on Google?',
          a: 'We build websites with clean technical SEO, schema tags, and location-targeted copy so search engines easily index what you do. For active top ranking, our Local SEO package is also available.',
        },
        {
          q: 'Can I add more features later?',
          a: 'Absolutely. All our solutions are modular. You can start with Onix Presence and later plug in booking systems, digital menus, or WhatsApp automations.',
        },
      ],
    },
    contact: {
      badge: 'Get in Touch',
      title: 'Tell us what your business needs',
      sub: 'Send a message via the form or reach out directly on WhatsApp.',
      formTitle: 'Request your free proposal',
      nameLabel: 'Your name:',
      namePlaceholder: 'E.g. Jane Doe',
      businessLabel: 'Business name or industry:',
      businessPlaceholder: 'E.g. Downtown Bistro / Dental Clinic',
      phoneLabel: 'Phone / WhatsApp:',
      phonePlaceholder: '+34 600 000 000',
      emailLabel: 'Email (optional):',
      solutionLabel: 'Which solution are you interested in?',
      solutionOptions: [
        { value: 'Onix Presència (des de 390 €)', label: 'Onix Presence (landing from 390 €)' },
        { value: 'Onix Sistema (des de 1.500 €)', label: 'Onix System (custom tool from 1,500 €)' },
        { value: 'SEO Local i Google Business', label: 'Local SEO and Maps ranking' },
        { value: 'Carta Digital Interactiva QR', label: 'Interactive QR Digital Menu' },
        { value: 'Assessorament i Diagnòstic', label: 'Not sure yet, I need advice' },
      ],
      messageLabel: 'Briefly tell us what you need:',
      messagePlaceholder: 'E.g. We need a modern website with opening hours and direct WhatsApp booking...',
      submitBtn: 'Send inquiry',
      submittingBtn: 'Sending...',
      privacyNote: 'By submitting you agree to our privacy policy. We respond within 24 hours.',
      successTitle: 'Inquiry received successfully!',
      successDesc: 'Thank you! We will get in touch within 24 hours to guide you.',
      openWhatsAppBtn: 'Chat on WhatsApp',
      directBadge: 'Direct contact',
      directTitle: 'Prefer to talk directly?',
      directDesc: 'Message us on WhatsApp to discuss your project instantly.',
      whatsappBtn: 'Chat on WhatsApp',
      callBtn: 'Call +34 694 91 97 57',
      commitmentTitle: 'Our commitment',
      commitmentPoints: [
        'Clear, fixed upfront quote from day one',
        'No mandatory monthly fees for your landing page',
        'We answer every inquiry in less than 24 hours',
      ],
    },
    footer: {
      tagline: 'Websites, artificial intelligence, and digital systems built for local business growth.',
      solutionsTitle: 'Solutions & Services',
      solutionsList: ['Onix Presence (from 390 €)', 'Onix System (from 1,500 €)', 'Local SEO & Google Business', 'QR Digital Menus', 'WhatsApp Automation'],
      navTitle: 'Navigation',
      linksList: ['Solutions', 'Why a website', 'ROI Calculator', 'Pricing', 'Contact'],
      contactBadge: 'Direct contact',
      phoneLabel: 'Phone:',
      rights: '© 2026 Onix Digital. All rights reserved.',
      legal: ['Privacy Policy', 'Legal Notice', 'Cookie Policy'],
    },
    whatsapp: {
      tooltip: 'Chat on WhatsApp',
      message: 'Hello, I would like information regarding Onix Digital web solutions for my business.',
    },
    marquee: ['Onix Presence from 390 €', 'Onix System from 1,500 €', 'Websites for local businesses', 'No mandatory monthly fees', 'Optimized for Google & AI'],
    stats: {
      badge: 'In numbers',
      deliveryLabel: 'Delivery time',
      deliverySub: 'From brief to production',
      projectsLabel: 'Completed projects',
      projectsSub: 'Websites and digital tools',
      automationsLabel: 'Active automations',
      automationsSub: 'Automated bookings and inquiries',
      uptimeLabel: 'Uptime',
      uptimeSub: 'Guaranteed 24/7 reliability',
    },
    testimonials: {
      quote: 'Owning our website allowed us to stop relying solely on social algorithms and receive steady bookings.',
      name: 'Charles Miller',
      role: 'Local Business Owner',
      metric: '+180% inquiries',
    },
  },
};
